import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { truncateText } from "../../utils/truncateText";
import { DropDown } from "@/icons/dropdown";
import styles from "./css/SelectField.module.css";

interface ListObject {
  name: string;
  [key: string]: any;
  icon?: React.ComponentType;
}

interface SelectButtonProps<T extends string | ListObject | undefined> {
  data: T[];
  updateFunction: (value: T | undefined) => void;
  disabledState?: boolean;
  selectedValue: T;
  size?: "small" | "large" | "xs" | "xs-s" | "small-w";
  placeholder?: string;
  top?: boolean;
  showEmpty?: boolean;
  bgGray?: boolean;
  maxHeight?: string;
  "data-testid"?: string;
}

const SelectField = <T extends string | ListObject | undefined>({
  data,
  updateFunction,
  disabledState = false,
  selectedValue,
  size = "large",
  placeholder = "",
  top = false,
  showEmpty = true,
  bgGray = false,
  maxHeight = "",
  "data-testid": dataTestId
}: SelectButtonProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({});
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLUListElement | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);

  const getDisplayValue = (value: T) => {
    if (value) {
      return typeof value === "string" ? value : value.name;
    }
    return "";
  };

  const truncatedValue = truncateText(getDisplayValue(selectedValue), 22);

  const updateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const position = {
      left: buttonRect.left + window.scrollX,
      width: buttonRect.width,
      position: "absolute" as const,
      zIndex: 9999,
    };

    if (top) {
      return {
        ...position,
        bottom: window.innerHeight - buttonRect.top + window.scrollY,
      };
    } else {
      return {
        ...position,
        top: buttonRect.bottom + window.scrollY,
      };
    }
  }, [top]);

  const handleToggle = () => {
    if (!disabledState) {
      const newPosition = updateDropdownPosition();
      if (newPosition) {
        setDropdownStyles(newPosition);
      }
      setIsOpen(prev => !prev);
    }
  };

  const handleSelect = (value: T) => {
    setIsOpen(false);
    const isStr = data.length !== 0 && typeof data[0] === "string";
    if (value === "" && !isStr) {
      updateFunction(undefined);
    } else {
      updateFunction(value);
    }
    setHighlightedIndex(null);
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node) &&
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    const handleScrollOrResize = () => {
      if (isOpen) {
        const newPosition = updateDropdownPosition();
        if (newPosition) {
          setDropdownStyles(newPosition);
        }
      }
    };

    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen, handleClickOutside, updateDropdownPosition]);

  const renderItem = (item: T) => {
    if (item) {
      return typeof item === "string" ? item : item.name;
    }
    return "";
  };

  const renderItemWithIcon = (item: T) => {
    if (!item || typeof item === "string") return renderItem(item);
    
    const Icon = item.icon;
    return (
      <div className={styles["item-with-icon"]}>
        <div className="flex flex-row gap-1">
        {Icon && <Icon />}
        <span>{item.name}</span>
        </div>
      </div>
    );
  };

  const isSelected = (item: T) => {
    if (typeof item === "string" && typeof selectedValue === "string") {
      return item === selectedValue;
    }
    if (typeof item === "object" && typeof selectedValue === "object") {
      return item.name === selectedValue.name;
    }
    return false;
  };

  const dropdown = (
    <ul
      ref={dropdownRef}
      className={`${styles.dropdown} ${top ? styles["dropdown-top"] : ""}`}
      style={{...dropdownStyles, maxHeight: maxHeight,overflowY: 'auto'}}
    >
      {showEmpty && (
        <li
          key="empty-option"
          className={`${styles["dropdown-item"]} ${styles["dropdown-item-small"]} ${styles["dropdown-item-hover"]}`}
          onClick={() => handleSelect("" as unknown as T)}
          data-testid={`${dataTestId}-empty`}
        >
          <span className="text-transparent">empty</span>
        </li>
      )}
      {data.map((item, index) => (
        <li
          key={index}
          className={`${styles["dropdown-item"]} ${size === "small" ? styles["dropdown-item-small"] : styles["dropdown-item-large"]} ${styles["dropdown-item-hover"]} ${isSelected(item) ? styles["dropdown-item-selected"] : ""} ${highlightedIndex === index ? "bg-gray-200" : ""}`}
          onClick={() => handleSelect(item)}
          data-testid={`${dataTestId}-${item}`}
        >
          {renderItemWithIcon(item)}
        </li>
      ))}
    </ul>
  );

  return (
    <div ref={containerRef} className={styles.container}>
      <div
        ref={buttonRef}
        data-testid={dataTestId}
        className={`${styles.button} ${bgGray? styles.gray: ""} ${size === "small" ? styles["button-small"] : size === "xs" ? styles.xs :  size === "xs-s"? styles["button-xs-s"]: size === "small-w"? styles["button-small-w"]: styles["button-large"]} ${disabledState ? styles["button-disabled"] : styles["button-enabled"]} focus:outline-none`}
        onClick={handleToggle}
        tabIndex={disabledState ? -1 : 0}
      >
        <span
          className={`${styles["text-enabled"]} ${disabledState ? styles["text-disabled"] : ""} ml-auto`}
        >
          {selectedValue ? renderItemWithIcon(selectedValue) : <span className="placeholder">{placeholder}</span>}
        </span>
        <span
          className={`${styles["dropdown-icon-wrapper"]} ${disabledState ? styles["text-disabled"] : ""}`}
        >
          <DropDown
            className={`${styles["dropdown-icon"]} ${isOpen ? styles["dropdown-icon-open"] : ""} ${disabledState ? "text-gray-400" : "text-gray-600"}`}
          />
        </span>
      </div>
      {isOpen && createPortal(dropdown, document.body)}
    </div>
  );
};

export default SelectField;
